<template>
    <div class="insights-dashboard mx-2" v-if="canView('insights')">
        <div v-if="!isConnected()" class="alert alert-danger mb-5 d-flex align-items-center">
            <img src="/img/icons/avid-icons/alert-circle.svg" class="me-3" style="width: 40px;" alt="">
            <div>
                <h2 class="mb-1">Your reports are disconnected!</h2>
                <p class="mb-0">One of the connections necessary to refresh these reports has been disconnected. <Link :href="'/settings/'+client.url+'/integrations'">Please reconnect to continue using these reports.</Link></p>
            </div>
        </div>

        <div v-if="local_report_pages.length > 0 && insight_metrics.length > 0" class="row mb-3">
            <h2 class="col-md-6 col-9 px-0 pb-2">Today's Top Five Insights</h2>
            <div class="col-md-6 col-3 px-0 pb-2">
                <Link v-if="local_report_pages.length > 0 && canExport('insights') " :href="'/insights/'+client.url+'/generate_ppt'" class="btn btn-primary float-end mb-2" id="export-button" >
                    <img class="icon pe-0" src="/img/icons/avid-icons/presentation-chart-01.svg" alt="">
                    <span class="ps-1 d-none d-md-block">Generate Presentation</span>
                </Link>
            </div>

            <div class="row top-5-metrics mx-0 px-0">
                <div class="col-lg-2 col-12 p-0">
                    <nav class="navbar nav-tab-side flex-column h-100 py-0" :class="{'overflow-auto': true}">
                        <ul class="navbar-nav w-100 flex-column h-100">
                            <li v-for="(metric, index) in insight_metrics" :key="'metric-'+index" 
                                class="nav-item flex-fill d-flex align-items-center justify-content-center metric-tab" 
                                :class="{'active-tab': metric.is_tab_active}">
                                <a class="nav-link w-100 h-100 d-flex flex-column justify-content-center align-items-center" 
                                   href="#" role="button" aria-expanded="false" @click.prevent="setActiveTab(index)">
                                    <div v-if="metric.percentage_changed != null" class="metric-value-container mb-2">
                                        <h2 class="metric-value">{{ formatValue(metric) }}</h2>
                                        <span class="badge mx-0 metric-badge d-xxl-inline-block d-block" 
                                              :class="{ 'bg-green bg-icon-increase': metric.percentage_changed > 0, 
                                                      'bg-red bg-icon-decrease': metric.percentage_changed < 0}">
                                            {{ percentage_na(metric.percentage_changed) }}
                                        </span>
                                    </div>
                                    <h4 class="border-bottom-1 text-center" v-else>{{ metric.metric_name }}</h4>
                                    <p class="mb-0 text-center lh-sm" v-if="metric.percentage_changed != null">{{ metric.metric_name }}</p>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div class="col-lg-10 col-12 p-0">
                    <div v-for="(metric, index) in insight_metrics" :key="'chart-'+metric.section_id" class="card" 
                         v-show="metric.is_tab_active" :style="!shouldShowSummary ? { minHeight: '500px' } : { minHeight: '200px' }">
                        <div class="card-body" v-if="metric.chart != null" :class="{'d-flex flex-column': !shouldShowSummary}">
                            <div class="row">
                                <div class="col-8">
                                    <h3><em>{{ metric.section_title }}:</em> {{ metric.chart.title }}</h3>
                                    <p class="font-color-tertiary">{{ metric.chart.subtitle }}</p>
                                </div>
                                <div class="col-4">
                                    <Link :href="'/insights/'+client.url+'/'+ metric.page_url + '/' + metric.section_id" 
                                       class="btn btn-secondary float-end">
                                        <img src="/img/icons/avid-icons/bar-chart-10.svg" class="icon" alt="">View Insights
                                    </Link>
                                </div>
                            </div>
                            <div class="row" :class="{'flex-grow-1': !shouldShowSummary}">
                                <div class="col" :class="{'mb-3': shouldShowSummary, 'd-flex flex-column': !shouldShowSummary}">
                                    <div :class="{'flex-grow-1': !shouldShowSummary, 'insights-dashboard-chart-full-height': !shouldShowSummary}" :style="!shouldShowSummary ? { minHeight: '400px' } : {}">
                                        <chart-template 
                                            :chart="metric.chart.chart" 
                                            :id="'chart-'+metric.chart.id" 
                                            :key="'chart-' + metric.chart.id" 
                                            :show_title="false"
                                            :allow_deep_dive="false"
                                            :current_size="'large'"
                                            :chart_show_data_labels="true"
                                            :show_compressed_numbers="user?.preferences?.show_compressed_numbers !== false"
                                        />
                                    </div>
                                </div>
                            </div>
                            <transition name="fade-slow">
                                <div v-if="shouldShowSummary && hasMetricSummary(metric)" class="d-flex summary">
                                    <div class="primary-chart-insight-icon me-3">
                                        <img class="icon" src="/img/icons/avid-icons/edna.svg" alt="">
                                    </div>
                                    <div class="mt-2">
                                        <h4>What does this mean?</h4>
                                        <div v-html="metric.summary"></div>
                                    </div>
                                </div>
                                <div v-else-if="shouldShowSummary && isMetricSummaryPending(metric)" class="d-flex summary">
                                    <div class="primary-chart-insight-icon me-3">
                                        <img src="/img/images/loading_edna.gif" class="icon" style="width: 24px; height: 24px;" ></img>
                                    </div>
                                    <div>
                                        <h4>Your data is being analyzed.</h4>
                                        <p>The results will be displayed here once the analysis is complete.</p>
                                    </div>
                                </div>
                                <div v-else-if="shouldShowSummary && metric.summary_status === 'failed'" class="d-flex summary">
                                    <div class="primary-chart-insight-icon me-3">
                                        <img class="icon" src="/img/icons/avid-icons/warning-circle.svg" alt="">
                                    </div>
                                    <div>
                                        <h4>We couldn&apos;t generate this summary.</h4>
                                        <p>There was a problem analyzing this insight. Please try refreshing again later.</p>
                                    </div>
                                </div>
                                <div v-else-if="shouldShowSummary" class="d-flex summary">
                                    <div class="primary-chart-insight-icon me-3">
                                        <img class="icon" src="/img/icons/avid-icons/note-2.svg" alt="">
                                    </div>
                                    <div>
                                        <h4>No summary is available.</h4>
                                        <p>There isn&apos;t an AI summary to display for this insight yet.</p>
                                    </div>
                                </div>
                            </transition>
                        </div>
                        <div v-else class="card-body">
                            <div class="asset-overlay">
                                <div class="overlay"></div>
                                <div class="cta">
                                    <div class="row d-flex align-items-center mx-3">
                                        <div class="col-2">
                                            <div class="spinner-border text-white" role="status">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                            <h4 class="text-white mt-2">Loading...</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div v-if="local_report_pages.length > 0" class="p-0 mt-3" id="available-reports">
                <div class="row mb-2">
                    <div class="col-lg-6 col-12 d-flex align-items-center">
                        <h3 class="px-0 mb-0">All Available Reports</h3>
                    </div> 

                    <div class="col-lg-6 d-none d-lg-block">
                        <div class="d-flex justify-content-end justify-self-end gap-2 mb-2">
                            <div class="position-relative">
                                <input type="text" 
                                       placeholder="Search by Name..." 
                                       aria-label="Search by Name..." 
                                       v-model="search_key" 
                                       @input="handleReportFilterSelection()"
                                       autocomplete="one-time-code" 
                                       class="search-box bg-white form-control me-1 py-2 box-shadowed"> 
                                <i class="fa fa-search search-placeholder-icon" v-if="search_key === ''"></i>
                            </div>
                            <button class="btn btn-secondary me-1 py-0 dropdown-toggle dropdown-toggle-no-btn box-shadowed" 
                                    data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                                <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary p-3"> 
                                <label class="form-label">Report Category:</label>
                                <select class="form-select" v-model="report_filters.group_name" @change="handleReportFilterSelection()" aria-label="Report Category:">
                                    <option value="all">*** All Categories ***</option>
                                    <option v-for="group in unique_group_names" :key="group" :value="group">{{ group }}</option>
                                </select>
                            </div>
                        </div>
                    </div> 
                </div>
                
                <!-- No results message -->
                <div v-if="page_by_group.length === 0 && (search_key !== '' || report_filters.group_name !== 'all')" class="row">
                    <div class="col-12">
                        <div class="card p-4 text-center">
                            <h3>No matching reports found</h3>
                            <p class="mb-3">We couldn't find any reports matching your search criteria.</p>
                            <div class="d-flex justify-content-center gap-3">
                                <button class="btn btn-secondary" @click="clearSearchAndFilters">
                                    <img class="icon" src="/img/icons/avid-icons/x-circle.svg" alt="">Clear Search and Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="row g-3 mb-5" v-else>
                    <div class="col-12 col-md-6 col-lg-4 col-xxl-3">
                        <div class="card chart-container d-flex flex-column h-100 p-3">
                            <div class="multi-metric-scorecard-template scorecard-template">
                                <div class="row" :class="{ 'time-period-three-metrics': meta_data && meta_data.subscriber }">
                                    <div class= "col-12 mb-3">
                                        <div class="metric-item">
                                            <div class="metric-icon-section passive-purple">
                                                <img src="/img/icons/avid-icons/calendar.svg" class="metric-icon icon-purple" alt="">
                                            </div>
                                            <div class="metric-info-section">
                                                <p class="metric-name mb-0">Time Period</p>
                                                <div class="metric-value-container">
                                                    <p class="mb-0">{{ client.default_reporting_period }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class= "col-12 mb-3">
                                        <div class="metric-item">
                                            <div class="metric-icon-section passive-green">
                                                <img src="/img/icons/avid-icons/metric-icon-other.svg" class="metric-icon icon-green" alt="">
                                            </div>
                                            <div class="metric-info-section">
                                                <p class="metric-name mb-0">Last Transaction Date</p>
                                                <div class="metric-value-container">
                                                    <p v-if="meta_data.constituent != null" class="mb-0">
                                                        <span v-if="meta_data.constituent._avid_max_display_date != null && meta_data.constituent._avid_max_display_date.date != null">{{date(meta_data.constituent._avid_max_display_date.date) }} </span>
                                                        <span v-else-if="meta_data.constituent._avid_max_display_date != null">
                                                            {{date(meta_data.constituent._avid_max_display_date) }} </span>
                                                        <span v-else-if="meta_data.constituent._max_overall_donation_date != null">
                                                            {{date(meta_data.constituent._max_overall_donation_date) }} 
                                                        </span>
                                                    </p>
                                                    <div v-else>
                                                        <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                                                            <span class="visually-hidden">Loading...</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="meta_data && meta_data.subscriber" class="col-12 mb-0">
                                        <div class="metric-item">
                                            <div class="metric-icon-section passive-yellow">
                                                <img src="/img/icons/avid-icons/metric-icon-other.svg" class="metric-icon icon-yellow" alt="">
                                            </div>
                                            <div class="metric-info-section">
                                                <p class="metric-name mb-0">Last Email Date</p>
                                                <div class="metric-value-container">
                                                    <p class="mb-0">{{ date(meta_data.subscriber._max_overall_record_date) }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-for="(group, index) in page_by_group" :key="'group-'+index" class="col-12 col-md-6 "
                    :class="group.pages.length > 5 ? 'col-lg-6 col-xxl-6' : 'col-lg-4 col-xxl-3'">
                        <div v-if="group.pages.length > 1" class="insights-report-card h-100" :class="getCardColorClass(group)">
                            <div class="d-flex align-items-center mb-4">
                                <div class="insights-report-card-icon me-3">
                                    <img :src="getGroupIcon(group)" :alt="group.group_name" class="icon ">
                                </div>
                                <h3 class="insights-report-card-title">{{ group.group_name }}</h3>                                
                            </div>
                            <ul :class="group.pages.length > 5 ? 'double-column' : 'single-column'">
                                <li v-for="(page, pageIndex) in group.pages" :key="'page-'+pageIndex" class="report-page-item">
                                    <Link :href="'/insights/'+client.url+'/'+ page.url" class="insights-report-card-link">
                                        {{ formatPageDisplayName(page.name) }}
                                    </Link>
                                    <a
                                        v-if="showAdminDeleteAction"
                                        class="ms-1 d-inline-block insights-report-card-link"
                                        aria-label="Delete report page"
                                        @click.prevent.stop="confirmDeleteReportPage(page)"
                                    >
                                        &times;
                                </a>
                                </li>
                            </ul>
                        </div>
                        <div v-else class="insights-report-card h-100" :class="getCardColorClass(group)">
                            <div class="d-flex align-items-center mb-3">
                                <div class="insights-report-card-icon me-3">
                                    <img :src="getGroupIcon(group)" :alt="group.group_name" class="icon ">
                                </div>
                                <h3 class="insights-report-card-title">{{ group.group_name }}</h3>                                
                            </div>
                            <p class="insights-report-card-description mt-2">{{ group.pages[0].description }}</p>
                            <div class="mt-4"> 
                                <Link :href="'/insights/'+client.url+'/'+ group.pages[0].url" class="btn view-btn btn-sm px-4">
                                    View Report
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else>
                <div class="card p-3 d-flex flex-row flex-grow-1">
                    <div class="px-1 pt-1 pb-0">
                        <h2 class="text-center mb-3">No reports have been created for this client yet.</h2>
                    </div>
                </div>
            </div>     
        </div>
    </div>
</template>


<script>
import Swal from 'sweetalert2';
import tablePaginationMixin from '../../mixins/table_pagination';
import permissionsMixin from '../../mixins/permissions';
import { defineComponent } from 'vue';
import { Link } from '@inertiajs/vue3';
import { marked } from 'marked';
import { useUserStore } from '../../stores/user';

export default defineComponent({
    components: {
        Swal,
        Link
    },
    setup() {
        const userStore = useUserStore();
        return {
            userStore
        };
    },
    props: {
        prioritizations: {
            type: Array,
            required: true
        },
        report_pages: {
            type: Array,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    mixins: [tablePaginationMixin, permissionsMixin],
    data() {
        return {
            page_by_group: [],
            original_page_by_group: null,
            local_report_pages: [],
            time_period:['monthly', 'quarterly', 'yearly', 'fiscal_year'],
            current_time_period: 'monthly',
            insight_metrics:[],
            meta_data: [],
            group: null,
   
            // Data for reports table
            filtered_report_pages: [],
            search_key: '',
            sort_key: 'name',
            sort_order: 'asc',
            report_filters: {
                group_name: 'all'
            },
            unique_group_names: [],
            window_width: window.innerWidth,
            isModifierKeyPressed: false,
            echoChannel: null, // Store Echo channel for cleanup
            sectionIndexMap: {}, // Map section_id to insight_metrics index
            _isUnmounting: false, // Flag to prevent updates during unmount
        };
    },
    beforeMount() {
        this.local_report_pages = JSON.parse(JSON.stringify(this.report_pages));
        this.loadReportPageByGroupName();
        this.loadPrioritization();
        this.loadMetaData();
    },
    mounted() {
        this.initializeTooltips();
        window.addEventListener('resize', this.updateWindowWidth);
        window.addEventListener('keydown', this.handleModifierKeyState);
        window.addEventListener('keyup', this.handleModifierKeyState);
        window.addEventListener('blur', this.clearModifierKeyState);
        this.setupKeyTakeawaysListener();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateWindowWidth);
        window.removeEventListener('keydown', this.handleModifierKeyState);
        window.removeEventListener('keyup', this.handleModifierKeyState);
        window.removeEventListener('blur', this.clearModifierKeyState);
        
        // Set a flag to prevent Echo callbacks from running
        this._isUnmounting = true;
        
        // Clean up Echo listener
        if (this.echoChannel) {
            this.echoChannel.stopListening('KeyTakeawaysUpdated');
            this.echoChannel = null;
        }
    },
    computed: {
        user() {
            return this.userStore.getCurrentUser;
        },
        shouldShowSummary() {
            return this.user?.preferences?.show_ai_summaries !== false;
        },
        showAdminDeleteAction() {
            return this.user?.user_type === 'admin' && this.isModifierKeyPressed;
        },
        getGroupColumnClass() {
            return (group) => {
                // Bootstrap sm breakpoint is 576px
                if (this.window_width < 576) {
                    return '';
                }
                if (group.pages.length >= 8) {
                    return 'triple-column';
                }
                if (group.pages.length < 5) {
                    return 'single-column';
                }
                return 'double-column';
            };
        }
    },
    methods: {
        hasSummaryContent(summary) {
            return typeof summary === 'string' && summary.trim() !== '';
        },
        hasMetricSummary(metric) {
            return this.hasSummaryContent(metric?.summary);
        },
        isMetricSummaryPending(metric) {
            return !metric?.summary_status || ['loading', 'queued'].includes(metric.summary_status);
        },
        handleModifierKeyState(event) {
            this.isModifierKeyPressed = event.metaKey || event.ctrlKey;
        },
        clearModifierKeyState() {
            this.isModifierKeyPressed = false;
        },
        confirmDeleteReportPage(page) {
            Swal.fire({
                title: "Are you sure you want to delete this report?",
                text: "This report will be removed.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    window.axios.delete('/api/insights/page/' + page.id).then(() => {
                        this.local_report_pages = this.local_report_pages.filter(reportPage => reportPage.id !== page.id);
                        this.loadReportPageByGroupName();
                        this.handleReportFilterSelection();

                        Swal.fire({
                            title: "Deleted",
                            text: "The report page was deleted successfully.",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        });
                    });
                }
            });
        },
        reorderPagesForTwoColumnDisplay(pages) {
            if (!Array.isArray(pages) || pages.length <= 5) {
                return pages;
            }

            const rowCount = Math.ceil(pages.length / 2);
            const reorderedPages = [];

            for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
                const leftColumnIndex = rowIndex;
                const rightColumnIndex = rowIndex + rowCount;

                if (leftColumnIndex < pages.length) {
                    reorderedPages.push(pages[leftColumnIndex]);
                }

                if (rightColumnIndex < pages.length) {
                    reorderedPages.push(pages[rightColumnIndex]);
                }
            }

            return reorderedPages;
        },
        initializeTooltips() {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            for (var i = 0; i < tooltipTriggerList.length; i++) {
              (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                  tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                  tooltipList[index].hide();
                });
              })(i);
            }
        },
        loadMetaData() {
            var data = {
              client: this.client,
              blend_type: 'constituent'
            };

            window.axios.post('/api/bigquery/get_metadata', data)
              .then(response => {
               this.meta_data['constituent'] = response.data.metadata;
               this.$forceUpdate();
               this.$nextTick(() => {
                   this.initializeTooltips();
               });
            });

            var data = {
              client: this.client,
              blend_type: 'subscriber'
            };

            window.axios.post('/api/bigquery/get_metadata', data)
              .then(response => {
               this.meta_data['subscriber'] = response.data.metadata;
               this.$forceUpdate();
               this.$nextTick(() => {
                   this.initializeTooltips();
               });
            });
        },
        loadPrimaryChartData(prioritization_index) {
            var data = {
              client_id: this.client.id,
              section_id: this.prioritizations[prioritization_index].section.id,
              page_id: this.prioritizations[prioritization_index].section.page.id
            };

            window.axios.post('/api/chart/get_primary_chart', data)
              .then(response => {
                this.prioritizations[prioritization_index].section = response.data.section;
                this.prioritizations[prioritization_index].section.primary_chart = response.data.chart;
                this.insight_metrics[prioritization_index].chart = response.data.chart;

                if (this.hasSummaryContent(response.data.section.summary)) {
                    this.insight_metrics[prioritization_index].summary = response.data.section.summary;
                    this.insight_metrics[prioritization_index].summary_status = 'completed';
                    // Also fetch takeaways to get related_charts for processing chart references
                    this.fetchSummary(prioritization_index, this.prioritizations[prioritization_index].section);
                } else {
                    this.fetchSummary(prioritization_index, this.prioritizations[prioritization_index].section);
                }
                this.$forceUpdate();
                this.$nextTick(() => {
                    this.initializeTooltips();
                });
            });
        },
        loadPrioritization() {
            for(var i = 0; i < this.prioritizations.length; i++) {
                if(this.prioritizations[i].section.display_value_name != null){
                    var metric_name = this.prioritizations[i].section.display_value_name;
                }
                else if(this.prioritizations[i].section.display_value_metric != null) {
                    var metric_name = this.prioritizations[i].section.display_value_metric.name;
                    if(metric_name.includes('('))
                        metric_name = metric_name.split('(')[0];
                }

                this.loadPrimaryChartData(i);

                this.insight_metrics.push(
                {
                    metric_name: (metric_name != null ? metric_name.trim() : ""),
                    page_title: this.prioritizations[i].section.page.name,
                    section_title: this.prioritizations[i].section.title,
                    section_id: this.prioritizations[i].report_page_section_id,
                    page_url: this.prioritizations[i].page_url,
                    value: this.prioritizations[i].display_value,
                    format: (this.prioritizations[i].section.display_value_metric != null) ? this.prioritizations[i].section.display_value_metric.format : null,
                    percentage_changed: this.prioritizations[i].prioritization_value,
                    chart: this.prioritizations[i].section.primary_chart,
                    summary: null,
                    summary_status: 'loading',
                    related_charts: [],
                    is_tab_active: (i == 0) ? true : false
                });
            }
        },
        isConnected() {

            //Look through this.client.data_blends to see if any have is_connected set to false
            for(var i = 0; i < this.client.data_blends.length; i++) {
                if(this.client.data_blends[i].is_connected == false) {
                    return false;
                }
            }
            return true;
        },
        loadReportPageByGroupName(){
            // First, sort report pages: by group order, then by page order, then alphabetically by name
            const sortedPages = [...this.local_report_pages].sort((a, b) => {
                // Get group order (default to 99 if no group or no order)
                const aGroupOrder = (a.group && a.group.order != null) ? a.group.order : 99;
                const bGroupOrder = (b.group && b.group.order != null) ? b.group.order : 99;
                
                // First sort by group order
                if (aGroupOrder !== bGroupOrder) {
                    return aGroupOrder - bGroupOrder;
                }
                
                // If same group order, sort by group name alphabetically
                const aGroupName = a.group_name || '';
                const bGroupName = b.group_name || '';
                if (aGroupName !== bGroupName) {
                    return aGroupName.localeCompare(bGroupName);
                }
                
                // Same group, sort by page order
                const aPageOrder = a.order || 0;
                const bPageOrder = b.order || 0;
                if (aPageOrder !== bPageOrder) {
                    return aPageOrder - bPageOrder;
                }
                
                // Same order, sort alphabetically by page name
                const aName = a.name || '';
                const bName = b.name || '';
                return aName.localeCompare(bName);
            });
            
            var page_group_array = [];
            var page_group_name = null;
            var unique_groups = new Set();

            var page_in_group_temp = [];
            
            sortedPages.forEach(page => {
                if(page_group_name && page?.group_name != page_group_name){
                    // Sort pages within the group: by order first, then alphabetically by name
                    page_in_group_temp.sort((a, b) => {
                        const aOrder = a.order || 0;
                        const bOrder = b.order || 0;
                        if (aOrder !== bOrder) {
                            return aOrder - bOrder;
                        }
                        const aName = a.name || '';
                        const bName = b.name || '';
                        return aName.localeCompare(bName);
                    });
                    page_in_group_temp = this.reorderPagesForTwoColumnDisplay(page_in_group_temp);
                    
                    page_group_array.push({
                        group_name: page_group_name,
                        pages: page_in_group_temp,
                        order: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.order : 99,
                        color: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.color : null,
                        icon: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.icon : null,
                    });
                    page_group_name = page.group_name;
                    page_in_group_temp = [page];
                } else {
                    page_group_name = page.group_name;
                    page_in_group_temp.push(page);
                }
                
                unique_groups.add(page.group_name);
            });

            // Sort the last group's pages
            page_in_group_temp.sort((a, b) => {
                const aOrder = a.order || 0;
                const bOrder = b.order || 0;
                if (aOrder !== bOrder) {
                    return aOrder - bOrder;
                }
                const aName = a.name || '';
                const bName = b.name || '';
                return aName.localeCompare(bName);
            });
            page_in_group_temp = this.reorderPagesForTwoColumnDisplay(page_in_group_temp);

            page_group_array.push({
                group_name: page_group_name,
                pages: page_in_group_temp,
                order: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.order : 99,
                color: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.color : null,
                icon: (page_in_group_temp[0] && page_in_group_temp[0].group != null) ? page_in_group_temp[0].group.icon : null,
            });
            
            // Sort groups: by order first, then alphabetically by group name
            page_group_array.sort((a, b) => {
                const aOrder = a.order || 99;
                const bOrder = b.order || 99;
                if (aOrder !== bOrder) {
                    return aOrder - bOrder;
                }
                const aName = a.group_name || '';
                const bName = b.group_name || '';
                return aName.localeCompare(bName);
            });
            
            this.unique_group_names = Array.from(unique_groups).sort();

            for(let i = 0; i < page_group_array.length; i++){
                if(page_group_array[i].pages.length == 1){
                    let currentSection = page_group_array[i].pages[0].sections[0];
                    var data = {
                        client_id: this.client.id,
                        section_id: currentSection.id,
                        page_id: page_group_array[i].pages[0].id
                    };
                    window.axios.post('/api/chart/get_primary_chart', data)
                      .then(response => {
                        currentSection.primary_chart = response.data.chart;
                        this.$forceUpdate();
                        this.$nextTick(() => {
                            this.initializeTooltips();
                        });
                    });
                }
            }

            this.page_by_group = page_group_array;
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        setActiveTab(index){
            this.insight_metrics.forEach((metric, i) =>{
                if (i == index){
                    metric.is_tab_active = true;
                } else {
                    metric.is_tab_active = false;
                }
            });
            this.$forceUpdate();
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        formatValue(metric){
            var self = this;
            if(metric.format == 'money' && metric.value < 100000)
                return self.currency(metric.value);
            if(metric.format == 'money')
                return self.currency_abbr(metric.value);
            if(metric.format == 'number' && metric.value < 10000)
                return self.number(metric.value);
            if(metric.format == 'number' )
                return self.num_abbr(metric.value);
            if(metric.format == 'percent')
                return self.percentage(metric.value);
        },
        fetchSummary(index, section){
            var form_data = {
                'section':section
            }

            // Map section_id to index for the client-wide listener
            if (section && section.id) {
                this.sectionIndexMap[section.id] = index;
            }

            window.axios.post('/api/insights/section/'+section.id+'/takeaways', form_data)
            .then(response => {
                if(response.data.status == 'completed'){
                    const summary = response.data.takeaways?.summary ?? null;
                    this.insight_metrics[index].summary = summary;
                    this.insight_metrics[index].summary_status = this.hasSummaryContent(summary) ? 'completed' : 'empty';
                    this.insight_metrics[index].related_charts = response.data.takeaways?.related_charts || [];
                    // Process summary to add clickable chart references
                    if (this.hasSummaryContent(summary)) {
                        this.processChartReferencesForMetric(index);
                    }
                } else if (response.data.status == 'queued' && !this.hasMetricSummary(this.insight_metrics[index])) {
                    this.insight_metrics[index].summary_status = 'queued';
                }
                // If status is 'queued', the client-wide listener will handle the update
            }).catch(error => {
                if (!this.hasMetricSummary(this.insight_metrics[index])) {
                    this.insight_metrics[index].summary = null;
                    this.insight_metrics[index].summary_status = 'failed';
                }
                this.insight_metrics[index].related_charts = [];
            });
        },
        setupKeyTakeawaysListener() {
            // Set up a single client-wide listener for all key takeaways updates
            // Demo account uses client.id === 0; 0 is falsy so we must check explicitly
            const clientId = this.client?.id;
            if (clientId !== 0 && !clientId) {
                return;
            }

            const self = this;
            this.echoChannel = window.Echo.private('client.' + clientId)
                .listen('KeyTakeawaysUpdated', (e) => {
                    // Guard against component being destroyed or unmounting
                    try {
                        // Check if component is unmounting or already destroyed
                        if (self._isUnmounting || !self || !self.insight_metrics || !self.$el) {
                            return;
                        }

                        // Filter by section_id and find the matching index
                        const sectionId = e.section_id;
                        const index = self.sectionIndexMap[sectionId];
                        
                        if (index === undefined || !self.insight_metrics[index]) {
                            return;
                        }

                        if (e.status == 'completed') {
                            const summary = e.takeaways?.summary ?? null;
                            self.insight_metrics[index].summary = summary;
                            self.insight_metrics[index].summary_status = self.hasSummaryContent(summary) ? 'completed' : 'empty';
                            self.insight_metrics[index].related_charts = e.takeaways.related_charts || [];
                            // Process summary to add clickable chart references
                            if (self.hasSummaryContent(summary)) {
                                self.processChartReferencesForMetric(index);
                            }
                            
                            // Only update if component is still mounted
                            if (!self._isUnmounting && self.$el) {
                                self.$forceUpdate();
                            }
                        } else if (e.status == 'failed' || e.status == 'timeout') {
                            if (!self.hasMetricSummary(self.insight_metrics[index])) {
                                self.insight_metrics[index].summary = null;
                                self.insight_metrics[index].summary_status = 'failed';
                                self.insight_metrics[index].related_charts = [];
                            }
                            
                            // Only update if component is still mounted
                            if (!self._isUnmounting && self.$el) {
                                self.$forceUpdate();
                            }
                        }
                    } catch (error) {
                        // Component likely destroyed, silently fail
                        console.warn('Echo callback attempted after component destruction:', error);
                    }
                });
        },
        handleReportFilterSelection() {
            let filtered_groups = [];
            
            if (!this.original_page_by_group) {
                this.original_page_by_group = JSON.parse(JSON.stringify(this.page_by_group));
            }
            
            this.original_page_by_group.forEach(group => {
                if (this.report_filters.group_name !== 'all' && 
                    group.group_name !== this.report_filters.group_name) {
                    return;
                }
                
                const filteredGroup = JSON.parse(JSON.stringify(group));
                
                if (this.search_key !== '') {
                    const searchLower = this.search_key.toLowerCase();
                    
                    filteredGroup.pages = group.pages.filter(page => {
                        const pageNameMatch = page.name.toLowerCase().includes(searchLower);
                        const pageDescMatch = page.description.toLowerCase().includes(searchLower);
                        const groupNameMatch = group.group_name.toLowerCase().includes(searchLower);
                        return pageNameMatch || pageDescMatch || groupNameMatch;
                    });
                    
                    if (filteredGroup.pages.length === 0) {
                        return;
                    }
                    
                    // Re-sort filtered pages: by order first, then alphabetically by name
                    filteredGroup.pages.sort((a, b) => {
                        const aOrder = a.order || 0;
                        const bOrder = b.order || 0;
                        if (aOrder !== bOrder) {
                            return aOrder - bOrder;
                        }
                        const aName = a.name || '';
                        const bName = b.name || '';
                        return aName.localeCompare(bName);
                    });
                }
                filteredGroup.pages = this.reorderPagesForTwoColumnDisplay(filteredGroup.pages);
                
                filtered_groups.push(filteredGroup);
            });
            
            // Sort filtered groups: by order first, then alphabetically by group name
            filtered_groups.sort((a, b) => {
                const aOrder = a.order || 99;
                const bOrder = b.order || 99;
                if (aOrder !== bOrder) {
                    return aOrder - bOrder;
                }
                const aName = a.group_name || '';
                const bName = b.group_name || '';
                return aName.localeCompare(bName);
            });
            
            this.page_by_group = filtered_groups;
            this.$forceUpdate();
            this.$nextTick(() => {
                this.initializeTooltips();
            });
        },
        clearSearchAndFilters() {
            this.search_key = '';
            this.report_filters.group_name = 'all';
            this.handleReportFilterSelection();
        },
        updateWindowWidth() {
            this.window_width = window.innerWidth;
        },
        formatPageDisplayName(name) {
            if (!name) return name;
            let display = name.replace(/ Channel Overview$/i, '');
            if (display.includes('Giving Level')) {
                const parenIndex = display.indexOf(' (');
                if (parenIndex !== -1) {
                    display = display.slice(0, parenIndex) + ' Donors';
                }
            }
            return display;
        },
        getGroupIcon(group) {
            // Use the icon from group data, or default icon
            if (group.icon) {
                // Check if it's a full path or just filename
                if (group.icon.startsWith('/')) {
                    return group.icon;
                }
                return '/img/icons/avid-icons/' + group.icon;
            }
            // Default icon
            return '/img/icons/avid-icons/chart-infographic.svg';
        },
        getCardColorClass(group) {
            // console.log(group);
            if(!group || !group.color || group.color === '' || group.color === null) {
                return 'color-purple';
            }
            // Normalize the color name to lowercase and trim whitespace to match CSS classes
            const normalizedColor = group.color.toLowerCase().trim();
            return 'color-' + normalizedColor;
        },
        processChartReferencesForMetric(index) {
            const metric = this.insight_metrics[index];
            if (!metric.summary || !metric.related_charts || metric.related_charts.length === 0) {
                return;
            }

            let processedSummary = metric.summary;

            // Strip any existing chart reference links (they may have wrong IDs from cache)
            processedSummary = processedSummary.replace(/<a[^>]*class="chart-reference-link"[^>]*>(.*?)<\/a>/gi, '$1');

            // Create a map of chart titles to chart data for quick lookup
            const chartMap = {};
            metric.related_charts.forEach(chart => {
                if (chart.chart_title) {
                    chartMap[chart.chart_title] = chart;
                }
            });

            // Sort chart titles by length in descending order (longest first)
            // This ensures "Revenue per Donor" is matched before "Revenue"
            const sortedChartTitles = Object.keys(chartMap).sort((a, b) => b.length - a.length);

            // Replace chart title mentions with clickable links
            sortedChartTitles.forEach(chartTitle => {
                const chart = chartMap[chartTitle];
                
                // Ensure we're using actual database IDs, not array positions
                const chartId = parseInt(chart.chart_id, 10);
                
                // Validate ID is actual database ID (not array index)
                if (!chartId || chartId <= 0) {
                    console.warn('Invalid chart reference ID:', chart);
                    return; // Skip invalid IDs
                }
                
                // Escape special regex characters in chart title (excluding * since titles shouldn't have them)
                const escapedTitle = chartTitle.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
                // Create regex to find chart title mentions, accounting for markdown formatting (**, *, etc.)
                // This matches the title even if wrapped in markdown like **title** or *title*
                // Pattern: optional markdown asterisks (0-2) before, then the title, then optional markdown asterisks (0-2) after
                // Use word boundaries to ensure we match the full title
                const regex = new RegExp(`(\\*{0,2})(?<!<a[^>]*>)(?<!["'])\\b${escapedTitle}\\b(?![^<]*</a>)(\\*{0,2})`, 'gi');
                
                // Create link to report page with chart hash
                const reportUrl = `/insights/${this.client.url}/${metric.page_url}#${chartId}`;
                
                processedSummary = processedSummary.replace(regex, (match) => {
                    // Replace the entire match (including markdown) with just the link (no markdown in link text)
                    return `<a href="${reportUrl}" class="chart-reference-link">${chartTitle}</a>`;
                });
            });

            // Convert any remaining markdown to HTML (after chart references are processed)
            processedSummary = this.markdownToHtml(processedSummary);

            metric.summary = processedSummary;
        },
        markdownToHtml(text) {
            if (!text) {
                return text;
            }

            // Use marked to properly parse markdown (handles nested bold/italic correctly)
            let html = marked.parse(text, { breaks: true });
            
            // Remove any remaining asterisks that weren't converted (cleanup for orphaned asterisks)
            html = html.replace(/\*/g, '');
            
            return html;
        }
    }   
})
</script>
